import { NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { LucideChevronDown, LucidePanelLeftClose, LucidePanelLeftOpen } from '@lucide/angular';
import { cn, MMSCAN_UI_I18N } from '@mmscan/angular-ui/core';
import { MmscanIcon } from '@mmscan/angular-ui/icon';
import { MmscanTooltip } from '@mmscan/angular-ui/tooltip';
import type { MmscanNavItem } from './nav-item';

let nextId = 0;

/**
 * Vertical navigation: links with icons, groups that expand (disclosure buttons), the active
 * page marked with `aria-current="page"`. `collapsed` shows icons only, with the labels kept for
 * screen readers and shown as tooltips; `collapsible` adds the toggle next to the heading. On small screens put it in an `mmscan-drawer`.
 * Replaces the old `mmscan-sidebar` (p-menu / p-speeddial).
 *
 * ```html
 * <mmscan-sidebar [items]="section" heading="Access Control" collapsible [(collapsed)]="rail" />
 * ```
 */
@Component({
  selector: 'mmscan-sidebar',
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, MmscanIcon, MmscanTooltip],
  template: `
    <nav [attr.aria-label]="heading() ? null : ariaLabel()" [attr.aria-labelledby]="heading() ? headingId : null">
      @if (heading() || collapsible()) {
        <div class="flex items-center gap-2 pb-2" [class.justify-center]="collapsed()">
          @if (heading()) {
            <h2 [id]="headingId" [class]="headingClasses()">{{ heading() }}</h2>
          }
          @if (collapsible()) {
            <button
              type="button"
              class="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-2 focus-visible:outline-ring"
              [class.ml-auto]="!collapsed()"
              [attr.aria-label]="i18n().toggleSidebar"
              [attr.aria-expanded]="!collapsed()"
              [attr.aria-controls]="listId"
              [mmscanTooltip]="i18n().toggleSidebar"
              tooltipPosition="right"
              (click)="collapsed.set(!collapsed())"
            >
              <mmscan-icon [icon]="collapsed() ? expandIcon : collapseIcon" size="md" />
            </button>
          }
        </div>
      }
      <ul [id]="listId" class="flex flex-col gap-0.5">
        @for (item of items(); track $index; let index = $index) {
          <li>
            @if (item.children?.length) {
              <button
                type="button"
                [class]="itemClasses()"
                [attr.aria-expanded]="isOpen(index)"
                [attr.aria-controls]="groupId(index)"
                [mmscanTooltip]="item.label"
                [tooltipDisabled]="!collapsed()"
                tooltipPosition="right"
                (click)="toggle(index)"
              >
                @if (item.icon) {
                  <mmscan-icon [icon]="item.icon" size="md" [class.text-primary-text]="accentIcons()" />
                }
                <span [class]="labelClasses()">{{ item.label }}</span>
                @if (!collapsed()) {
                  <mmscan-icon
                    [icon]="chevron"
                    size="sm"
                    class="ml-auto text-muted-foreground transition-transform"
                    [class.rotate-180]="isOpen(index)"
                  />
                }
              </button>
              <ul
                [id]="groupId(index)"
                class="mt-0.5 flex flex-col gap-0.5"
                [class.pl-7]="!collapsed()"
                [hidden]="!isOpen(index)"
              >
                @for (child of item.children; track $index) {
                  <li>
                    <ng-container *ngTemplateOutlet="link; context: { $implicit: child }" />
                  </li>
                }
              </ul>
            } @else {
              <ng-container *ngTemplateOutlet="link; context: { $implicit: item }" />
            }
          </li>
        }
      </ul>
    </nav>

    <ng-template #link let-item>
      @if (item.route) {
        <a
          [routerLink]="item.route"
          routerLinkActive
          [routerLinkActiveOptions]="{ exact: !!item.exact }"
          ariaCurrentWhenActive="page"
          [attr.aria-disabled]="item.disabled || null"
          [attr.tabindex]="item.disabled ? -1 : null"
          [class]="itemClasses()"
          [mmscanTooltip]="item.label"
          [tooltipDisabled]="!collapsed()"
          tooltipPosition="right"
        >
          @if (item.icon) {
            <mmscan-icon [icon]="item.icon" size="md" [class.text-primary-text]="accentIcons()" />
          }
          <span [class]="labelClasses()">{{ item.label }}</span>
        </a>
      } @else if (item.url) {
        <a
          [href]="item.url"
          [attr.aria-disabled]="item.disabled || null"
          [attr.tabindex]="item.disabled ? -1 : null"
          [class]="itemClasses()"
          [mmscanTooltip]="item.label"
          [tooltipDisabled]="!collapsed()"
          tooltipPosition="right"
        >
          @if (item.icon) {
            <mmscan-icon [icon]="item.icon" size="md" [class.text-primary-text]="accentIcons()" />
          }
          <span [class]="labelClasses()">{{ item.label }}</span>
        </a>
      } @else {
        <button
          type="button"
          [class]="itemClasses()"
          [disabled]="item.disabled"
          [mmscanTooltip]="item.label"
          [tooltipDisabled]="!collapsed()"
          tooltipPosition="right"
          (click)="item.command?.()"
        >
          @if (item.icon) {
            <mmscan-icon [icon]="item.icon" size="md" [class.text-primary-text]="accentIcons()" />
          }
          <span [class]="labelClasses()">{{ item.label }}</span>
        </button>
      }
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class MmscanSidebar {
  private readonly baseId = `mmscan-sidebar-${nextId++}`;
  protected readonly chevron = LucideChevronDown;
  private readonly openGroups = signal<ReadonlySet<number>>(new Set());

  readonly items = input.required<readonly MmscanNavItem[]>();
  readonly ariaLabel = input<string>();
  /** Section title shown above the items; also names the navigation landmark. */
  readonly heading = input<string>();
  /** Icons only; labels stay available to screen readers and as tooltips. Two-way bindable. */
  readonly collapsed = model(false);
  /** Adds a button next to the heading that collapses and expands the sidebar. */
  readonly collapsible = input(false, { transform: booleanAttribute });
  /** Colour item icons with the primary colour. */
  readonly accentIcons = input(false, { transform: booleanAttribute });
  protected readonly headingId = `${this.baseId}-heading`;
  protected readonly listId = `${this.baseId}-list`;
  protected readonly i18n = inject(MMSCAN_UI_I18N);
  protected readonly collapseIcon = LucidePanelLeftClose;
  protected readonly expandIcon = LucidePanelLeftOpen;

  protected readonly itemClasses = computed(() =>
    cn(
      'flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-foreground outline-none',
      'hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
      'aria-[current=page]:bg-primary/10 aria-[current=page]:text-primary-text',
      this.collapsed() && 'justify-center px-2'
    )
  );
  protected readonly headingClasses = computed(() =>
    this.collapsed() ? 'sr-only' : 'min-w-0 truncate px-3 py-1 text-base font-bold text-muted-foreground'
  );
  protected readonly labelClasses = computed(() => (this.collapsed() ? 'sr-only' : 'truncate'));

  protected groupId(index: number): string {
    return `${this.baseId}-group-${index}`;
  }

  protected isOpen(index: number): boolean {
    return this.openGroups().has(index);
  }

  protected toggle(index: number): void {
    this.openGroups.update((groups) => {
      const next = new Set(groups);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }
}
